import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useWindowSize } from "lib/hooks";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Nav, Container, Row, Col, Card } from "react-bootstrap";
import {
  getAccountReducerResponse,
  getAnalysesReducerResponse,
  getTeamsReducerResponse,
  getAccountUsersReducerResponse,
  getJwtReducerResponse,
} from "store/selectors";
import {
  getAccountAction,
  getAccountUsersAction,
  getTeamsAction,
  getAnalysesAction,
  decodeJwtAction,
} from "store/actions/actionCreators";
import MainNav from "components/nav/MainNav";
import { Breadcrumb, BreadcrumbData, Page } from "@avlino-inc/alenza-react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import {
  Account,
  AccountUser,
  Team,
  Analysis,
  JWT,
  GetAnalysesRequestType,
  GetTeamsRequestType,
  GetAccountUsersRequestType,
  GetAccountRequestType,
} from "types";
import * as routes from "routes";
import TeamsTable from "components/dashboard/team/TeamsTable";
import AnalysesTable from "components/dashboard/analysis/AnalysesTable";
import AccountUsersTable from "components/dashboard/account/AccountUsersTable";
import styles from "./AccountPage.module.scss";
import {
  InfoCard,
  InfoTableCard,
  alenzaStats, // Analyrses
  alenzaUsers, // Team
} from "@avlino-inc/alenza-react";
import {
  faUsersClass, // Account Users
} from "@fortawesome/pro-light-svg-icons/faUsersClass";

const AccountPage = ({
  accountName,
  authed,
}: {
  accountName: string;
  authed: Promise<boolean>;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const windowSize = useWindowSize();

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("AccountPage useEffect()...");
    dispatch(decodeJwtAction({}));
    const accountRequest: GetAccountRequestType = {
      params: {
        name: accountName,
      },
    };
    const analysesRequest: GetAnalysesRequestType = {
      params: {
        account_name: accountName,
      },
    };
    const teamsRequest: GetTeamsRequestType = {
      params: {
        account_name: accountName,
      },
    };
    const accountUsersRequest: GetAccountUsersRequestType = {
      params: {
        account_name: accountName,
      },
    };
    dispatch(getAccountAction(accountRequest));
    dispatch(getAnalysesAction(analysesRequest));
    dispatch(getTeamsAction(teamsRequest));
    dispatch(getAccountUsersAction(accountUsersRequest));
  }, [dispatch, accountName]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  const accountData: Account = useSelector((state) =>
    getAccountReducerResponse(state, "data")
  );
  const analysesData: Analysis[] = useSelector((state) =>
    getAnalysesReducerResponse(state, "data")
  );
  const teamsData: Team[] = useSelector((state) =>
    getTeamsReducerResponse(state, "data")
  );
  const accountUsersData: AccountUser[] = useSelector((state) =>
    getAccountUsersReducerResponse(state, "data")
  );
  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );

  const [showAnalyses, setShowAnalyses] = useState<boolean>(true);
  const [showTeams, setShowTeams] = useState<boolean>(false);
  const [showAccountUsers, setShowAccountUsers] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(false);

  const teamRowEvent = {
    onClick: (e: any, row: Team, rowIndex: number) => {
      history.push(routes.ACCOUNT_TEAM(accountName, row.name.toLowerCase()));
    },
  };

  const analysisRowEvent = {
    onClick: (e: any, row: Analysis, rowIndex: number) => {
      history.push(
        `/account/${accountName}/analysis/${row.name.toLowerCase()}`
      );
    },
  };

  const accountUserRowEvent = {
    onClick: (e: any, row: Team, rowIndex: number) => {},
  };

  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.DASHBOARD_MAIN,
        label: "Command Center",
      },
      {
        url: routes.ACCOUNTS,
        label: "Accounts",
      },
      {
        url: routes.ACCOUNT(accountName),
        label: `${accountName}`,
      },
    ],
    []
  );

  const generateComponentSelectionVerticalNav = () => {
    const menuItemClass = "account-page-navbar__menu-item";
    const menuItemSelectedClass = "account-page-navbar__menu-item-selected";
    return (
      <>
        <Nav className="flex-column component-selection-nav">
          <Nav.Link
            className={[
              styles[menuItemClass],
              showAnalyses ? styles[menuItemSelectedClass] : "",
            ].join(" ")}
            onClick={() => {
              setShowAnalyses(true);
              setShowTeams(false);
              setShowAccountUsers(false);
            }}
          >
            Analyses
          </Nav.Link>
          <Nav.Link
            className={[
              styles[menuItemClass],
              showTeams ? styles[menuItemSelectedClass] : "",
            ].join(" ")}
            onClick={() => {
              setShowAnalyses(false);
              setShowTeams(true);
              setShowAccountUsers(false);
            }}
          >
            Teams
          </Nav.Link>
          <Nav.Link
            className={[
              styles[menuItemClass],
              showAccountUsers ? styles[menuItemSelectedClass] : "",
            ].join(" ")}
            onClick={() => {
              setShowAnalyses(false);
              setShowTeams(false);
              setShowAccountUsers(true);
            }}
          >
            Users
          </Nav.Link>
        </Nav>
      </>
    );
  };

  const generateComponentSelectionTabs = () => {
    const menuItemClass = "account-page-navbar__menu-item";
    const menuItemSelectedClass = "account-page-navbar__menu-item-selected";
    return (
      <Row
        className="justify-content-md-center"
        style={{ marginBottom: "20px" }}
      >
        <Nav
          justify
          className="w-100 justify-content-center"
          variant="tabs"
          defaultActiveKey="account-analyses"
        >
          <Nav.Item>
            <Nav.Link
              className={[
                styles[menuItemClass],
                showAnalyses ? styles[menuItemSelectedClass] : "",
              ].join(" ")}
              eventKey="account-analyses"
              onClick={() => {
                setShowAnalyses(true);
                setShowTeams(false);
                setShowAccountUsers(false);
              }}
            >
              Analyses
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              className={[
                styles[menuItemClass],
                showTeams ? styles[menuItemSelectedClass] : "",
              ].join(" ")}
              eventKey="account-teams"
              onClick={() => {
                setShowAnalyses(false);
                setShowTeams(true);
                setShowAccountUsers(false);
              }}
            >
              Teams
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              className={[
                styles[menuItemClass],
                showAccountUsers ? styles[menuItemSelectedClass] : "",
              ].join(" ")}
              eventKey="account-users"
              onClick={() => {
                setShowAnalyses(false);
                setShowTeams(false);
                setShowAccountUsers(true);
              }}
            >
              Users
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </Row>
    );
  };

  const getCardIcon = (
    showAccountUsers: boolean,
    showTeams: boolean,
    showAnalyses: boolean
  ): any => {
    if (showAccountUsers) {
      return faUsersClass;
    } else if (showTeams) {
      return alenzaUsers;
    } else if (showAnalyses) {
      return alenzaStats;
    } else {
      return null;
    }
  };

  const getTitle = (
    account: Account,
    showAccountUsers: boolean,
    showTeams: boolean,
    showAnalyses: boolean
  ): string => {
    if (showAccountUsers) {
      return `${account.name} Users`;
    } else if (showTeams) {
      return `${account.name} Teams`;
    } else if (showAnalyses) {
      return `${account.name} Analyses`;
    } else {
      return account.name;
    }
  };

  return (
    <div id="AccountPage">
      <MainNav
        first_name={jwtPayloadData && jwtPayloadData.first_name}
        last_name={jwtPayloadData && jwtPayloadData.last_name}
        username={jwtPayloadData && jwtPayloadData.username}
        email={jwtPayloadData && jwtPayloadData.email}
      />
      <Container fluid={true} style={{ minWidth: "100%", margin: "15px" }}>
        <Page.Header>
          <Breadcrumb items={breadcrumbItems} onClickItem={history.push} />
        </Page.Header>
        {windowSize.width <= 768 ? generateComponentSelectionTabs() : null}
        <Row className="justify-content-md-center">
          {windowSize.width >= 768
            ? generateComponentSelectionVerticalNav()
            : null}
          <Col xs={12} md={10} lg={8} xl={6}>
            <InfoTableCard
              icon={getCardIcon(showAccountUsers, showTeams, showAnalyses)}
              color="bright-blue"
              onMenuClick={() =>
                showSearch ? setShowSearch(false) : setShowSearch(true)
              }
            >
              <Container>
                <Row className={styles["account-page-card__account-info"]}>
                  <Col>
                    <p
                      className={
                        styles["account-page-card__account-info__account-title"]
                      }
                    >
                      {accountData &&
                        getTitle(
                          accountData,
                          showAccountUsers,
                          showTeams,
                          showAnalyses
                        )}
                    </p>
                  </Col>
                </Row>
                {showAccountUsers && accountUsersData ? (
                  <>
                    <Row>
                      <Col>
                        <Card.Body>
                          <AccountUsersTable
                            accountUsers={accountUsersData}
                            rowEvents={accountUserRowEvent}
                            showEmail={true}
                            showPic={true}
                            cardTable={true}
                            showSearch={showSearch}
                          />
                        </Card.Body>
                      </Col>
                    </Row>
                  </>
                ) : null}
                {showTeams ? (
                  <>
                    <Row>
                      <Col>
                        <Card.Body>
                          <TeamsTable
                            teams={teamsData}
                            rowEvents={teamRowEvent}
                            showDescription={true}
                            cardTable={true}
                            showSearch={showSearch}
                          />
                        </Card.Body>
                      </Col>
                    </Row>
                  </>
                ) : null}
                {showAnalyses ? (
                  <>
                    <Row>
                      <Col>
                        <Card.Body>
                          <AnalysesTable
                            analyses={analysesData}
                            rowEvents={analysisRowEvent}
                            showDescription={true}
                            cardTable={true}
                            showSearch={showSearch}
                          />
                        </Card.Body>
                      </Col>
                    </Row>
                  </>
                ) : null}
              </Container>
            </InfoTableCard>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AccountPage;
